/**
 * Advertorial (template product.andador-adv) · comportamento
 *
 * - Rolagem suave dos botões com destino "#ancora" (ex.: #oferta), com foco acessível.
 * - <adv-sticky-cta>: barra fixa que leva à oferta. Web component: reinicializa sozinho
 *   quando o editor recarrega a seção e libera observadores ao ser removido.
 * - Perguntas frequentes: abre a pergunta selecionada no editor.
 *
 * Carregado como módulo: o navegador executa este arquivo uma única vez por página,
 * mesmo que várias seções o incluam.
 */

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function scrollToAnchor(hash) {
  const id = decodeURIComponent(hash.replace(/^#/, ''));
  if (!id) return false;
  const target = document.getElementById(id);
  if (!target) return false;

  target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  return true;
}

if (!window.__advLandingInit) {
  window.__advLandingInit = true;

  // Delegação: funciona para botões adicionados depois (editor, seções recarregadas).
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-adv-scroll]') : null;
    if (!link) return;
    const hash = link.getAttribute('href') || '';
    if (!hash.startsWith('#')) return;
    if (scrollToAnchor(hash)) {
      event.preventDefault();
      history.replaceState(null, '', hash);
    }
  });

  // Editor: abrir a pergunta selecionada.
  document.addEventListener('shopify:block:select', (event) => {
    const el = event.target;
    if (el instanceof HTMLDetailsElement && el.classList.contains('adv-faq__item')) el.open = true;
  });
}

class AdvStickyCta extends HTMLElement {
  #bar = null;
  #target = null;
  #frame = 0;
  #priceObserver = null;
  #abort = null;

  connectedCallback() {
    this.#bar = this.querySelector('.adv-sticky__bar');
    if (!this.#bar) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const schedule = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#update();
      });
    };

    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    // Seções recarregadas no editor podem recriar a âncora: busca de novo a cada atualização.
    document.addEventListener('shopify:section:load', schedule, { signal });

    this.#observePrice();
    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#priceObserver?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    document.body.classList.remove('adv-sticky-active');
  }

  #findTarget() {
    const id = this.dataset.target || 'oferta';
    return document.getElementById(id);
  }

  #update() {
    if (!this.#bar) return;
    const target = this.#findTarget();
    const minScroll = parseInt(this.dataset.minScroll || '0', 10) || 0;
    const enabledHere = getComputedStyle(this).display !== 'none';

    // Visível depois da rolagem mínima e enquanto a área de compra ainda não apareceu.
    let beforeOffer = true;
    if (target) beforeOffer = target.getBoundingClientRect().top > window.innerHeight * 0.9;

    const visible = enabledHere && window.scrollY > minScroll && beforeOffer;
    this.#bar.dataset.visible = String(visible);
    this.#bar.toggleAttribute('inert', !visible);

    if (visible) {
      document.body.style.setProperty('--adv-sticky-h', `${this.#bar.offsetHeight}px`);
    }
    document.body.classList.toggle('adv-sticky-active', visible);
  }

  /** Mantém o preço igual ao da área de compra quando o cliente troca de variante. */
  #observePrice() {
    const slot = this.querySelector('[data-adv-price]');
    const productId = this.dataset.productId;
    if (!slot || !productId) return;

    const copy = () => {
      const source = document.querySelector(
        `product-price[data-product-id="${productId}"] [ref="priceContainer"]`
      );
      if (!source) return;
      const html = source.innerHTML.trim();
      if (html && slot.innerHTML !== html) slot.innerHTML = html;
    };

    const source = document.querySelector(`product-price[data-product-id="${productId}"]`);
    const root = source?.closest('.shopify-section');
    if (!root) return;

    this.#priceObserver = new MutationObserver(() => copy());
    this.#priceObserver.observe(root, { childList: true, subtree: true, characterData: true });
  }
}

if (!customElements.get('adv-sticky-cta')) customElements.define('adv-sticky-cta', AdvStickyCta);

/* ---------- Abas expansíveis: abrir uma por vez ---------- */
if (!window.__advAccInit) {
  window.__advAccInit = true;
  document.addEventListener(
    'toggle',
    (event) => {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement) || !item.open || !item.classList.contains('adv-acc__item')) return;
      const group = item.closest('.adv-acc');
      if (!group || group.dataset.oneOpen !== 'true') return;
      group.querySelectorAll('details.adv-acc__item[open]').forEach((other) => {
        if (other !== item) other.open = false;
      });
    },
    true
  );

  document.addEventListener('shopify:block:select', (event) => {
    const el = event.target;
    if (el instanceof HTMLDetailsElement && el.classList.contains('adv-acc__item')) el.open = true;
  });
}

/* ---------- Vídeos em loop (estilo GIF): tocam só quando visíveis ---------- */
function setupLoopVideos(root = document) {
  const videos = root.querySelectorAll('.adv-media--video video[autoplay]:not([data-adv-watched])');
  if (!videos.length) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) v.play().catch(() => {});
          else v.pause();
        });
      }, { threshold: 0.2 })
    : null;
  videos.forEach((v) => {
    v.dataset.advWatched = '';
    v.muted = true;
    if (reduce) {
      v.removeAttribute('autoplay');
      v.pause();
      v.controls = true;
      return;
    }
    io?.observe(v);
  });
}
setupLoopVideos();
document.addEventListener('shopify:section:load', (e) => setupLoopVideos(e.target));
